import { ChevronRight } from 'baseui/icon';
import moment from 'moment';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { CareTypeNames } from '../../Components/CareTypeNames';
import { FinalButton } from '../../Components/FinalButton';
import { ListView } from '../../Components/ListView';
import { LoadingPage } from '../../Components/LoadingOverlay';
import { UniversityLogo } from '../../Components/Organization/UniversityLogo';
import { useCurrentProvider } from '../../Components/Permissions';
import { useCareTypeConfigContext } from '../../Components/CareTypeConfigContext';
import { PaddedPage, Text } from '../../globalStyles';
import { ReferralListQuery, useReferralListQuery } from '../../graphQL';
import { findStatusByCareType } from '../../utils/careTypeAppointmentStatuses';
import {
  compareDates,
  compareLastNames,
  getFullName,
  getFullNameReversed,
} from '../../modelUtils/users';
import { ArrayElement } from '../../types';
import { PageTitle } from '../Shared';

type ReferralUser = ArrayElement<ReferralListQuery['referrals']>;

export function ReferrerList() {
  const { data } = useReferralListQuery({
    variables: {
      fetchReferrer: false,
      fetchOrganization: false,
    },
  });
  const history = useHistory();
  const location = useLocation();
  const organizationId = location.pathname.split('/')[2];
  const { appView } = useCurrentProvider();
  const { getCareTypeDisplay } = useCareTypeConfigContext();
  const isReferrer = appView === 'referral';

  const columnInitialAppointment = {
    key: 'appts',
    title: 'Initial Appointment',
    render: (user: ReferralUser) => {
      const links = user.onHoldOrActiveCareTypes.map(careType => {
        const status = findStatusByCareType(user.careTypeAppointmentStatuses, careType);
        return {
          name: careType,
          serviceTitle: getCareTypeDisplay(careType).serviceTitle,
          done: !!(status?.upcomingAppointment || status?.completedAppointment),
        };
      });

      return (
        <div>
          {links.map(l => (
            <div key={l.name}>
              {l.done ? (
                <p>{l.serviceTitle} Booked</p>
              ) : (
                <Text.link
                  data-cy="referrer-schedule"
                  to={`/book?userId=${user.id}&careType=${l.name}`}
                  className="flex items-center b"
                >
                  Schedule {l.serviceTitle} <ChevronRight size={22} />
                </Text.link>
              )}
            </div>
          ))}
        </div>
      );
    },
  };

  if (!data) return <LoadingPage />;
  return (
    <PaddedPage>
      <UniversityLogo />
      <div className="flex flex-row items-center mb3">
        <PageTitle className="mb3">Students</PageTitle>
        <div className="ml3">
          <FinalButton
            pill
            iconLeft="iconsWhitePlusSvg"
            onClick={() => history.push(`/organizations/${organizationId}/students/enroll`)}
            data-cy="addStudentButton"
          >
            Add
          </FinalButton>
        </div>
      </div>
      <ListView
        data={data.referrals}
        persist
        columns={[
          {
            key: 'name',
            title: 'Name',
            render: user => (
              <div>
                <Text.bodyBold data-cy="name" className="ttc">
                  {getFullNameReversed(user)}
                </Text.bodyBold>
                <Text.captionGrey className="ttc">
                  <CareTypeNames careTypes={user.onHoldOrActiveCareTypes} separator=" & " />
                </Text.captionGrey>
              </div>
            ),
            sort: compareLastNames,
          },
          {
            key: 'createdAt',
            title: 'Date Referred',
            render: user => <div>{moment(user.createdAt).format('L')}</div>,
            sort: (a, b) => compareDates(a.createdAt?.toString(), b.createdAt?.toString()),
          },
          {
            key: 'email',
            title: 'Email',
            render: user => <div data-cy="email">{user.email}</div>,
            sort: (a, b) =>
              a.email?.toLowerCase().localeCompare(b.email?.toLowerCase() ?? '') ?? -1,
          },
          { ...columnInitialAppointment, hidden: isReferrer },
        ]}
        getKey={user => user.id}
        link={user => `/users/${user.id}`}
        search={(user, value) => {
          const lowerCaseValue = value.toLowerCase();
          return [
            getFullName(user).toLowerCase(),
            user.customerId?.toLowerCase() ?? '',
            user.email?.toLowerCase() ?? '',
          ].some(i => i.includes(lowerCaseValue));
        }}
        initialSortColumn="createdAt"
        initialSortDirection="desc"
      />
    </PaddedPage>
  );
}
